const D = console.log
const E = console.error

import * as net from './net'

/**
 * NORMALIZE path
 * @param path 
 */
function normalizePath (path:string) : string {
    // REMOVE leading/trailing/dupe slashes and leading/trailing whitespace
    return path.trim().replace(/^\/+/,'').replace(/\/+$/,'').replace(/\/+/g,'/').trim()
}

/**
 * PRIMITIVE wrapper of `localStorage` browser API.
 */
export class LocalStore {
    basepath: string

    constructor ({ basepath, }: { basepath: string })
    {
        basepath = normalizePath(basepath)
        if (!basepath) {
            throw Error(`basepath is empty`)
        }
        this.basepath = basepath
    }

    /**
     * Produce fully-qualified local storage path
     * 
     * @param {string} key storage item key
     */
    private path (key:string) : string {
        const path = `${this.basepath}/${key}`
        //D('LOCALSTORE PATH',path)
        return path
    }

    /**
     * Save artifact to storage
     * @param {string} key
     * @param {string} data string-encoded data
     */
    set ({key,data}:{key:string,data:string}) {
        localStorage.setItem(this.path(key),data)
    }

    /**
     * Load artifact from storage
     * @param {string} key
     */
    get (key:string) : string | null {
        return localStorage.getItem(this.path(key))
    }

    /**
     * Delete artifact from storage
     * @param {string} key
     */
    delete (key:string) {
        localStorage.removeItem(this.path(key))
    }

    /**
     * Artifact exist at path?
     */
    has (key:string) : boolean {
        return localStorage.getItem(this.path(key)) !== null
    }
}

/**
 * PRIMITIVE wrapper of Wolffhound Remote Storage API
 */
export class StoreAPI {

    url: URL
    timeout: number
    basepath: string

    constructor ({url,basepath,timeout}: {url: URL, basepath:string, timeout:number}) {
        url = net.UOurL.normalize(url)
        if (timeout <= 0) {
            throw Error(`timeout must be a non-zero positive integer: ${timeout}`)
        }
        this.url = url
        this.timeout = timeout
        basepath = normalizePath(basepath)
        if (!basepath) {
            throw StoreAPI.logErr(`StoreAPI.browsePath FAILED: basepath is empty: ${basepath}`)
        }
        this.basepath = basepath
    }

    /**
     * PUT (compressed) artifact to "path"
     */
    async put ({path,data} : {path:string,data:string}) {
        const url = this.artifactPath(path)
        
        // COMPRESS data w/ GZIP
        const stream = new Blob([new TextEncoder().encode(data)])
            .stream()
            .pipeThrough(new CompressionStream('gzip'))
        
        // CONVERT compressed stream to blob
        const body = await new Response(stream).blob()
        
        // PUT payload to URL
        const response = await net.UOurL.fetch({
            url,
            options: {
                method: 'PUT',
                body,
                headers: { 'Content-Type': 'application/octet-stream' },
            },
            timeout: this.timeout
        })
        
        if (!response.ok) {
            throw StoreAPI.logErr(`StoreAPI.put FAILED ${response.status} ${response.statusText} URL ${url}`)
        }
    }

    /**
     * DELETE named artifact at path
     */
    async remove (path:string) {
        const url = this.artifactPath(path)

        // PUT payload to URL
        const response = await net.UOurL.fetch({url, options: { method: 'DELETE' }, timeout:this.timeout})
        if (!response.ok) {
            throw StoreAPI.logErr(`StoreAPI.remove FAILED ${response.status} ${response.statusText} URL ${url}`)
        }
    }

    /**
     * GENERATE fully-qualified listing path
     * @param path 
     * @returns 
     */
    private listingPath ($path: string) : URL {
        const url = new URL(this.url)
        const path = normalizePath($path)
        if (!path) {
            throw StoreAPI.logErr(`StoreAPI.listingPath FAILED: path is empty: ${path}`)
        }
        // APPEND path component to base URL
        url.pathname = url.pathname 
            + '/browse'
            + `/${this.basepath}`
            + '/'
            + path
        D('LISTING PATH',url.pathname)
        return url
    }

    /**
     * GENERATE fully-qualified data artifact path
     * @param path 
     * @returns 
     */
    private artifactPath ($path: string) : URL {
        const url = new URL(this.url)
        const path = normalizePath($path)
        if (!path) {
            throw StoreAPI.logErr(`StoreAPI.artifactPath FAILED: path is empty: ${path}`)
        }
        // APPEND path component to base URL
        url.pathname = url.pathname
            + '/content'
            + `/${this.basepath}`
            + '/'
            + path
        D('ARTIFACT PATH',url.pathname)
        return url
    }

    /**
     * GET remote artifact at path
     */
    async get (path:string) : Promise<string> {
        D('REMOTE GET',path)
        const url = this.artifactPath(path)

        // FETCH (compressed) content
        const response = await net.UOurL.fetch({url,timeout:this.timeout})
        if (!response.ok) {
            throw StoreAPI.logErr(`StoreAPI.get FAILED ${response.status} ${response.statusText} URL ${url}`)
        }
        
        // DECOMPRESS response body
        const blob = response.body?.pipeThrough(new DecompressionStream("gzip"))
        // CONVERT to text
        const data = await new Response(blob).text()
        return data
    }

    static logErr (msg: string) : Error {
        E(msg)
        return new Error(msg)
    }

    /**
     * LIST remote artifacts at path
     */
    async list (path:string) : Promise<string[]> {
        D('REMOTE LIST',path)
        const url = this.listingPath(path)

        // FETCH artifact listing
        const response = await net.UOurL.fetch({url,timeout:this.timeout,acceptedStatuses:[404]})
        const {status} = response

        // NOT FOUND is legit status
        if (!response.ok && status !== 404) {
            throw StoreAPI.logErr(`StoreAPI.browse FAILED ${response.status} ${response.statusText} URL ${url}`)
        }

        const data = status === 404
            // NON-EXISTING path
            ? []
            : await response.json()
        if (Array.isArray(data)) {
            return data as string[]
        }
        else {
            throw StoreAPI.logErr(`StoreAPI.browse: JSON response is unexpected: ${data}`)
        }
    }
}
